import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Modal } from './ComponentLibrary';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useCurrency, SUPPORTED_CURRENCIES } from '../context/CurrencyContext';
import { SunIcon, MoonIcon } from './IconSet';

const RESOURCE_LINKS = [
  { label: 'Property Guide', path: '/blog' },
  { label: 'Help Centre', path: '/help' },
  { label: 'Safety Tips', path: '/safety' },
  { label: 'Seller Guide', path: '/seller-guide' },
  { label: 'Become an Agent', path: '/become-an-agent' },
];

function dashboardPathFor(hasRole, isAdmin) {
  if (isAdmin) {
    return '/admin';
  }
  if (hasRole('seller', 'agent')) {
    return '/agent';
  }
  return '/dashboard';
}

function MobileMenuSheet({ isOpen, onClose }) {
  const { user, hasRole, isAdmin, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { currency, setCurrency } = useCurrency();
  const navigate = useNavigate();

  const go = (path) => {
    onClose();
    navigate(path);
  };

  const initials = user?.name
    ? user.name
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase()
    : '?';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Menu" mobilePresentation="sheet" className="mobile-menu-sheet">
      {user ? (
        <div className="mobile-menu-sheet__profile">
          <span className="mobile-menu-sheet__avatar" aria-hidden="true">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt="" className="mobile-menu-sheet__avatar-image" />
            ) : (
              initials
            )}
          </span>
          <div>
            <div className="mobile-menu-sheet__who">{user.name}</div>
            <div className="mobile-menu-sheet__role">{user.role}</div>
          </div>
        </div>
      ) : (
        <div className="mobile-menu-sheet__auth-row">
          <button type="button" className="btn-primary" onClick={() => go('/login')}>
            Log in
          </button>
          <button type="button" className="secondary" onClick={() => go('/signup')}>
            Sign up
          </button>
        </div>
      )}

      {user && (
        <div className="mobile-menu-sheet__group">
          <button type="button" className="mobile-menu-sheet__item" onClick={() => go(dashboardPathFor(hasRole, isAdmin))}>
            <span className="mobile-menu-sheet__item-label">📊 Dashboard</span>
            <span aria-hidden="true">›</span>
          </button>
          <button type="button" className="mobile-menu-sheet__item" onClick={() => go('/favorites')}>
            <span className="mobile-menu-sheet__item-label">❤️ Favorites</span>
            <span aria-hidden="true">›</span>
          </button>
          <button type="button" className="mobile-menu-sheet__item" onClick={() => go('/settings')}>
            <span className="mobile-menu-sheet__item-label">⚙️ Settings</span>
            <span aria-hidden="true">›</span>
          </button>
          <button type="button" className="mobile-menu-sheet__item" onClick={() => go('/security')}>
            <span className="mobile-menu-sheet__item-label">
              {user.twoFactorEnabled ? '🔐 2FA enabled' : '🔓 Enable 2FA'}
            </span>
            <span aria-hidden="true">›</span>
          </button>
          {isAdmin && (
            <button type="button" className="mobile-menu-sheet__item" onClick={() => go('/admin')}>
              <span className="mobile-menu-sheet__item-label">⚙️ Admin</span>
              <span aria-hidden="true">›</span>
            </button>
          )}
          <button
            type="button"
            className="mobile-menu-sheet__item"
            onClick={() => {
              onClose();
              logout();
              navigate('/');
            }}
          >
            <span className="mobile-menu-sheet__item-label">Log out</span>
          </button>
        </div>
      )}

      <div className="mobile-menu-sheet__group">
        <div className="mobile-menu-sheet__group-label">Resources</div>
        {RESOURCE_LINKS.map((link) => (
          <button key={link.path} type="button" className="mobile-menu-sheet__item" onClick={() => go(link.path)}>
            <span className="mobile-menu-sheet__item-label">{link.label}</span>
            <span aria-hidden="true">›</span>
          </button>
        ))}
      </div>

      <div className="mobile-menu-sheet__group">
        <div className="mobile-menu-sheet__group-label">Currency</div>
        <div className="chip-row">
          {SUPPORTED_CURRENCIES.map(({ code, symbol }) => (
            <button
              key={code}
              type="button"
              className={`chip${code === currency ? ' chip--active' : ''}`}
              onClick={() => setCurrency(code)}
            >
              {symbol} {code}
            </button>
          ))}
        </div>
      </div>

      <div className="mobile-menu-sheet__group">
        <button type="button" className="mobile-menu-sheet__item" onClick={toggleTheme}>
          <span className="mobile-menu-sheet__item-label">
            {isDarkMode ? <SunIcon size={17} /> : <MoonIcon size={17} />} Theme
          </span>
          <span>{isDarkMode ? 'Dark' : 'Light'}</span>
        </button>
      </div>
    </Modal>
  );
}

MobileMenuSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MobileMenuSheet;
